import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Boxes, ChevronDown, ChevronUp, FileDown, Search, Pencil, AlertCircle, Trash2, Save, Settings2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import StockManager from "../components/StockManager";
import ProductEditor from "../components/ProductEditor";
import { useAuth } from "../contexts/AuthContext";

/**
 * Tally-style Stock Register — one row per product with expandable per-size
 * breakdown, Opening / Inwards / Outwards / Closing in qty AND value, plus a
 * "Daily count entry" toggle that swaps in the existing StockManager so the
 * stock-update employee keeps a single page for both reporting and editing.
 */
export default function StockRegister({ heading = "Stock Register", eyebrow = "Inventory · Tally-style" }) {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "editor";

    const today = new Date().toISOString().slice(0, 10);
    const monthStart = today.slice(0, 8) + "01";

    const [data, setData] = useState(null);
    const [from, setFrom] = useState(monthStart);
    const [to, setTo] = useState(today);
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");
    const [expanded, setExpanded] = useState({});
    const [busy, setBusy] = useState(false);
    const [view, setView] = useState("register"); // "register" | "daily"
    const [bulkMode, setBulkMode] = useState(false);
    // Per-cell pending edits keyed by `${product_id}::${size_id}`. Each value is
    // a partial patch: { closing_qty?, size_label?, sku?, rate? }. Only fields the
    // user has actually touched are present, so we can ship a minimal API payload.
    const [bulkEdits, setBulkEdits] = useState({});
    const [bulkBusy, setBulkBusy] = useState(false);
    const [cats, setCats] = useState([]);

    // Inline edit / delete dialogs
    const [editSize, setEditSize] = useState(null);     // { product, size }
    const [editQty, setEditQty] = useState("");
    const [editNote, setEditNote] = useState("");
    const [editLabel, setEditLabel] = useState("");     // size label
    const [editSku, setEditSku] = useState("");
    const [editHsn, setEditHsn] = useState("");
    const [editRate, setEditRate] = useState("");
    const [editDistRate, setEditDistRate] = useState("");
    const [deleteProduct, setDeleteProduct] = useState(null);
    const [editProductId, setEditProductId] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/categories").then((r) => setCats(r.data)).catch(() => setCats([]));
    }, []);

    const load = async () => {
        setBusy(true);
        try {
            const params = { from_date: from, to_date: to };
            if (category !== "all") params.category = category;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/admin/stock/register", { params });
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load stock register");
            setData({ rows: [], totals: {} });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { if (view === "register") load(); /* eslint-disable-next-line */ }, [from, to, category, view]);
    useEffect(() => {
        if (view !== "register") return;
        const t = setTimeout(load, q.trim() ? 400 : 0);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    /** True when ANY field has a pending edit. Computed each render — cheap. */
    const bulkDirtyKeys = useMemo(() => {
        return Object.keys(bulkEdits).filter((k) => {
            const patch = bulkEdits[k] || {};
            return Object.keys(patch).length > 0;
        });
    }, [bulkEdits]);

    /** Looks up the original value for a given key + field so we can diff. */
    const originalFor = (key, field) => {
        for (const r of rows) {
            for (const s of r.sizes) {
                if (`${r.product_id}::${s.size_id}` === key) {
                    if (field === "closing_qty") return s.closing_qty;
                    if (field === "size_label") return s.size_label || "";
                    if (field === "sku") return s.sku || "";
                    if (field === "rate") return s.rate || 0;
                }
            }
        }
        return null;
    };

    /** Read the current cell value: pending edit if any, else the original. */
    const cellValue = (key, field, original) => {
        const patch = bulkEdits[key];
        if (patch && patch[field] !== undefined) return patch[field];
        return original;
    };
    const setCell = (key, field, value) => {
        setBulkEdits((prev) => {
            const next = { ...prev };
            const patch = { ...(next[key] || {}) };
            const orig = originalFor(key, field);
            // Strip leading zeros / empty strings for numeric fields
            const isNumber = field === "closing_qty" || field === "rate";
            const cmp = isNumber ? parseFloat(value || 0) : (value || "");
            const origCmp = isNumber ? parseFloat(orig || 0) : (orig || "");
            if ((isNumber && Math.abs(cmp - origCmp) < 0.001) || (!isNumber && cmp === origCmp)) {
                delete patch[field];
            } else {
                patch[field] = value;
            }
            if (Object.keys(patch).length === 0) {
                delete next[key];
            } else {
                next[key] = patch;
            }
            return next;
        });
    };

    const saveBulk = async () => {
        if (bulkDirtyKeys.length === 0) return;
        setBulkBusy(true);
        try {
            // 1) Split dirty edits into stock-only (closing_qty) and variant-metadata
            //    (size_label / sku / rate). Group variant-metadata changes by product
            //    so we can fire ONE PUT per product instead of one per variant.
            const stockUpdates = [];
            const variantsByProduct = {};
            for (const key of bulkDirtyKeys) {
                const [product_id, size_id] = key.split("::");
                const patch = bulkEdits[key];
                if (patch.closing_qty !== undefined) {
                    const q = parseInt(patch.closing_qty, 10);
                    if (!Number.isNaN(q) && q >= 0) {
                        stockUpdates.push({ product_id, size_id, stock: q, note: "Inline edit from Stock Register" });
                    }
                }
                if (patch.size_label !== undefined || patch.sku !== undefined || patch.rate !== undefined) {
                    variantsByProduct[product_id] = variantsByProduct[product_id] || [];
                    variantsByProduct[product_id].push({ size_id, ...patch });
                }
            }

            // 2) Apply variant-metadata changes — one product PUT per product
            const productIds = Object.keys(variantsByProduct);
            for (const product_id of productIds) {
                // eslint-disable-next-line no-await-in-loop
                const { data: full } = await api.get(`/products/${product_id}`);
                const patches = variantsByProduct[product_id];
                const variants = (full.variants || []).map((v) => {
                    const p = patches.find((x) => x.size_id === v.size_id);
                    if (!p) return v;
                    const out = { ...v };
                    if (p.size_label !== undefined) out.size_label = p.size_label || v.size_label;
                    if (p.sku !== undefined) out.sku = p.sku === "" ? null : p.sku;
                    if (p.rate !== undefined) {
                        const r = parseFloat(p.rate);
                        out.price = Number.isNaN(r) ? v.price : r;
                    }
                    return out;
                });
                // eslint-disable-next-line no-await-in-loop
                await api.put(`/products/${product_id}`, {
                    name: full.name,
                    category_id: full.category_id,
                    short_description: full.short_description || "",
                    description: full.description || "",
                    indications: full.indications || "",
                    composition: full.composition || "",
                    dosage: full.dosage || "",
                    images: full.images || [],
                    variants,
                    active: !!full.active,
                    featured: !!full.featured,
                });
            }

            // 3) Apply stock changes in one batched call
            if (stockUpdates.length > 0) {
                await api.put("/admin/stock", { items: stockUpdates });
            }

            const variantCount = productIds.reduce((sum, p) => sum + variantsByProduct[p].length, 0);
            const parts = [];
            if (stockUpdates.length) parts.push(`${stockUpdates.length} stock`);
            if (variantCount) parts.push(`${variantCount} variant`);
            toast.success(`Saved · ${parts.join(" + ") || "0"} edit${(stockUpdates.length + variantCount) === 1 ? "" : "s"}`);
            setBulkEdits({});
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Bulk save failed");
        } finally {
            setBulkBusy(false);
        }
    };

    const exportXlsx = async () => {
        try {
            const params = { from_date: from, to_date: to };
            if (category !== "all") params.category = category;
            if (q.trim()) params.q = q.trim();
            const res = await api.get("/admin/stock/register/export.xlsx", { params, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `stock_register_${from}_to_${to}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export failed");
        }
    };

    const openEdit = (product, size) => {
        setEditSize({ product, size });
        setEditQty(String(size.closing_qty));
        setEditNote("");
        setEditLabel(size.size_label || "");
        setEditSku(size.sku || "");
        setEditHsn(size.hsn || "");
        setEditRate(String(size.rate || 0));
        setEditDistRate(""); // will be filled when product is fetched below if user expands
    };

    const saveStock = async () => {
        if (!editSize) return;
        const qty = parseInt(editQty, 10);
        if (Number.isNaN(qty) || qty < 0) return toast.error("Enter a valid quantity");
        const newRate = parseFloat(editRate);
        if (Number.isNaN(newRate) || newRate < 0) return toast.error("Enter a valid rate");

        setSaving(true);
        try {
            const variantChanged = (
                (editLabel || "") !== (editSize.size.size_label || "")
                || (editSku || "") !== (editSize.size.sku || "")
                || (editHsn || "") !== (editSize.size.hsn || "")
                || Math.abs(newRate - (editSize.size.rate || 0)) > 0.001
            );

            // 1. If any variant metadata changed, send a full product PUT
            if (variantChanged) {
                const { data: full } = await api.get(`/products/${editSize.product.product_id}`);
                const variants = (full.variants || []).map((v) => {
                    if (v.size_id !== editSize.size.size_id) return v;
                    return {
                        ...v,
                        size_label: editLabel || v.size_label,
                        sku: editSku === "" ? null : editSku,
                        hsn: editHsn === "" ? null : editHsn,
                        price: newRate,
                    };
                });
                const payload = {
                    name: full.name,
                    category_id: full.category_id,
                    short_description: full.short_description || "",
                    description: full.description || "",
                    indications: full.indications || "",
                    composition: full.composition || "",
                    dosage: full.dosage || "",
                    images: full.images || [],
                    active: !!full.active,
                    featured: !!full.featured,
                    variants,
                };
                await api.put(`/products/${editSize.product.product_id}`, payload);
            }

            // 2. Send stock update through the dedicated endpoint so register deltas log correctly
            const stockChanged = qty !== (editSize.size.closing_qty || 0);
            if (stockChanged) {
                await api.put("/admin/stock", {
                    items: [{
                        product_id: editSize.product.product_id,
                        size_id: editSize.size.size_id,
                        stock: qty,
                        note: editNote || undefined,
                    }],
                });
            }

            toast.success(
                variantChanged && stockChanged
                    ? `Variant + stock saved · ${editSize.product.product_name} ${editLabel}`
                    : variantChanged
                        ? `Variant saved · ${editSize.product.product_name} ${editLabel}`
                        : stockChanged
                            ? `Stock saved · ${editSize.product.product_name} ${editLabel}`
                            : "No changes"
            );
            setEditSize(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save changes");
        } finally {
            setSaving(false);
        }
    };

    const confirmDeleteProduct = async () => {
        if (!deleteProduct) return;
        setSaving(true);
        try {
            await api.delete(`/products/${deleteProduct.product_id}`);
            toast.success(`Deleted '${deleteProduct.product_name}'`);
            setDeleteProduct(null);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't delete product");
        } finally {
            setSaving(false);
        }
    };

    const presets = [
        { label: "This month", from: monthStart, to: today },
        { label: "Last month", ...lastMonth() },
        { label: "This quarter", ...quarter() },
        { label: "Financial year", ...fiscalYear() },
    ];

    const rows = useMemo(() => data?.rows || [], [data]);
    const totals = data?.totals || {};

    if (view === "daily") {
        return (
            <div>
                <Toaster position="top-center" />
                <div className="flex justify-end mb-4">
                    <Button onClick={() => setView("register")} variant="outline" className="rounded-none border-sky-700 text-sky-700" data-testid="back-to-register">
                        ← Back to Stock Register
                    </Button>
                </div>
                <StockManager heading="Daily stock entry" eyebrow="Inventory · physical count" />
            </div>
        );
    }

    return (
        <div data-testid="stock-register">
            <Toaster position="top-center" />

            {/* Header */}
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><Boxes className="w-3.5 h-3.5" />{eyebrow}</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">{heading}</h1>
                    <p className="text-sm text-stone-600 mt-2 max-w-2xl">
                        Opening · Inwards · Outwards · Closing per product with valuation. Inwards captures manual stock-in; Outwards captures both manual stock-down and packing auto-deductions.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {canManage && (
                        <Button
                            onClick={() => { setBulkMode(!bulkMode); setBulkEdits({}); if (!bulkMode) setExpanded({}); }}
                            variant={bulkMode ? "default" : "outline"}
                            className={`rounded-none ${bulkMode ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}`}
                            data-testid="toggle-bulk-mode"
                        >
                            <Pencil className="w-4 h-4 mr-2" />{bulkMode ? "Exit bulk edit" : "Bulk edit"}
                        </Button>
                    )}
                    <Button onClick={() => setView("daily")} className="rounded-none bg-amber-600 hover:bg-amber-700 text-white" data-testid="open-daily-entry">
                        <Pencil className="w-4 h-4 mr-2" />Daily count entry
                    </Button>
                    <Button onClick={exportXlsx} variant="outline" className="rounded-none border-stone-400" data-testid="export-register-xlsx">
                        <FileDown className="w-4 h-4 mr-2" />Excel
                    </Button>
                </div>
            </div>

            {/* Bulk-edit sticky save banner */}
            {bulkMode && (
                <div className="sticky top-0 z-20 bg-emerald-700 text-white px-4 py-3 mb-4 flex items-center justify-between flex-wrap gap-2 shadow-md" data-testid="bulk-banner">
                    <div className="text-sm">
                        <strong className="font-serif text-lg">Inline edit mode</strong>
                        <span className="ml-3 text-emerald-100">
                            {bulkDirtyKeys.length === 0
                                ? "Click any Size, SKU, Rate or Cl. Qty cell to edit. Values auto-recalculate. Op./In/Out columns are read-only (derived from past entries)."
                                : `${bulkDirtyKeys.length} variant${bulkDirtyKeys.length === 1 ? "" : "s"} edited`}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            onClick={() => { setBulkEdits({}); }}
                            disabled={bulkDirtyKeys.length === 0}
                            variant="outline"
                            className="rounded-none border-white text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-50"
                            data-testid="bulk-reset"
                        >
                            Reset
                        </Button>
                        <Button
                            onClick={saveBulk}
                            disabled={bulkBusy || bulkDirtyKeys.length === 0}
                            className="rounded-none bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            data-testid="bulk-save"
                        >
                            <Save className="w-4 h-4 mr-2" />
                            {bulkBusy ? "Saving…" : `Save ${bulkDirtyKeys.length || "all"} change${bulkDirtyKeys.length === 1 ? "" : "s"}`}
                        </Button>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-sky-100 p-4 mb-4 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">From</label>
                    <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="rounded-none border-stone-300 w-40 mt-1" data-testid="reg-from" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">To</label>
                    <Input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className="rounded-none border-stone-300 w-40 mt-1" data-testid="reg-to" />
                </div>
                <div className="flex flex-wrap gap-1">
                    {presets.map((p) => (
                        <button
                            key={p.label}
                            onClick={() => { setFrom(p.from); setTo(p.to); }}
                            className="text-[11px] uppercase tracking-widest px-3 py-1 border border-stone-300 hover:border-sky-700 hover:text-sky-700 transition-colors"
                            data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex gap-2 items-end">
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger className="rounded-none border-stone-300 w-48" data-testid="reg-category"><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search item / SKU / size…" className="rounded-none border-stone-300 pl-9 w-72" data-testid="reg-search" />
                    </div>
                </div>
            </div>

            {/* KPI strip */}
            {data && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    <KPI label="Opening value" value={formatINR(totals.opening_value)} sub={`${totals.opening_qty} units`} />
                    <KPI label="Inwards" value={formatINR(totals.inwards_value)} sub={`${totals.inwards_qty} units`} cls="text-emerald-700" />
                    <KPI label="Outwards" value={formatINR(totals.outwards_value)} sub={`${totals.outwards_qty} units`} cls="text-red-700" />
                    <KPI label="Closing value" value={formatINR(totals.closing_value)} sub={`${totals.closing_qty} units`} cls="text-sky-700" big />
                </div>
            )}

            {/* Register table */}
            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th></Th>
                            <Th>Item</Th>
                            <Th>Category</Th>
                            <Th cls="text-right" colSpan={2}>Opening</Th>
                            <Th cls="text-right" colSpan={2}>Inwards</Th>
                            <Th cls="text-right" colSpan={2}>Outwards</Th>
                            <Th cls="text-right" colSpan={2}>Closing</Th>
                            {canManage && <Th cls="text-right">Actions</Th>}
                        </tr>
                        <tr className="bg-sky-50/60 border-b border-sky-100 text-[10px] uppercase tracking-widest text-stone-500">
                            <th></th><th></th><th></th>
                            <th className="p-1 text-right">Qty</th><th className="p-1 text-right">Value</th>
                            <th className="p-1 text-right">Qty</th><th className="p-1 text-right">Value</th>
                            <th className="p-1 text-right">Qty</th><th className="p-1 text-right">Value</th>
                            <th className="p-1 text-right">Qty</th><th className="p-1 text-right">Value</th>
                            {canManage && <th></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {busy ? (
                            <tr><td colSpan={canManage ? 12 : 11} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : !rows.length ? (
                            <tr><td colSpan={canManage ? 12 : 11} className="p-12 text-center text-stone-500">No products match.</td></tr>
                        ) : rows.map((r) => {
                            // Auto-expand rows that have pending edits in bulk mode so the
                            // user can see what they've changed without scrolling and clicking
                            // every row. We deliberately do NOT auto-expand every row when bulk
                            // mode toggles on — that's a heavy render for catalogs >50 products.
                            const hasPendingEdit = bulkMode && r.sizes.some(
                                (s) => bulkEdits[`${r.product_id}::${s.size_id}`],
                            );
                            const open = hasPendingEdit || expanded[r.product_id];
                            const lowSize = r.sizes.find((s) => s.closing_qty <= 5);
                            return (
                                <React.Fragment key={r.product_id}>
                                    <tr className="border-b border-stone-100 hover:bg-sky-50/40 cursor-pointer" onClick={() => setExpanded({ ...expanded, [r.product_id]: !open })} data-testid={`reg-row-${r.product_id}`}>
                                        <td className="p-2 w-6">{open ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}</td>
                                        <td className="p-2 font-medium text-slate-900">
                                            {r.product_name}
                                            {lowSize && <AlertCircle className="w-3 h-3 inline ml-2 text-amber-600" title="Low closing stock on a variant" />}
                                        </td>
                                        <td className="p-2 text-xs text-stone-500">{r.category}</td>
                                        <Num n={r.opening_qty} /><Val v={r.opening_value} />
                                        <Num n={r.inwards_qty} cls="text-emerald-700" /><Val v={r.inwards_value} cls="text-emerald-700" />
                                        <Num n={r.outwards_qty} cls="text-red-700" /><Val v={r.outwards_value} cls="text-red-700" />
                                        <Num n={r.closing_qty} cls="font-semibold" /><Val v={r.closing_value} cls="text-sky-700 font-semibold" />
                                        {canManage && (
                                            <td className="p-2 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="inline-flex gap-1 justify-end">
                                                    <button
                                                        onClick={() => setEditProductId(r.product_id)}
                                                        className="text-amber-700 hover:bg-amber-50 border border-amber-300 p-1 inline-flex items-center"
                                                        data-testid={`reg-edit-product-${r.product_id}`}
                                                        title="Edit product (name, sizes, prices…)"
                                                    >
                                                        <Settings2 className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteProduct(r)}
                                                        className="text-red-700 hover:bg-red-50 border border-red-300 p-1 inline-flex items-center"
                                                        data-testid={`reg-delete-product-${r.product_id}`}
                                                        title="Delete product"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                    {open && (
                                        <tr className="bg-stone-50 border-b border-stone-200">
                                            <td colSpan={canManage ? 12 : 11} className="p-3">
                                                <table className="w-full text-xs">
                                                    <thead>
                                                        <tr className="border-b border-stone-200 text-stone-500">
                                                            <th className="text-left py-1">Size</th>
                                                            <th className="text-left">SKU</th>
                                                            <th className="text-right">Rate</th>
                                                            <th className="text-right">Op. Qty</th><th className="text-right">Op. Value</th>
                                                            <th className="text-right">In Qty</th><th className="text-right">In Value</th>
                                                            <th className="text-right">Out Qty</th><th className="text-right">Out Value</th>
                                                            <th className="text-right">Cl. Qty</th><th className="text-right">Cl. Value</th>
                                                            {canManage && <th className="text-right">Edit</th>}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {r.sizes.map((s) => {
                                                            const key = `${r.product_id}::${s.size_id}`;
                                                            const editable = bulkMode && canManage;
                                                            const edited = bulkDirtyKeys.includes(key);
                                                            const cellQty = parseFloat(cellValue(key, "closing_qty", s.closing_qty)) || 0;
                                                            const cellRate = parseFloat(cellValue(key, "rate", s.rate)) || 0;
                                                            const liveClosingValue = cellQty * cellRate;
                                                            return (
                                                                <tr key={s.size_id} className={`border-b border-stone-100 ${edited ? "bg-amber-50/40" : ""}`}>
                                                                    {/* SIZE */}
                                                                    <td className="py-1 font-mono">
                                                                        {editable ? (
                                                                            <input
                                                                                value={cellValue(key, "size_label", s.size_label || "")}
                                                                                onChange={(e) => setCell(key, "size_label", e.target.value)}
                                                                                onFocus={(e) => e.target.select()}
                                                                                className={`w-20 h-7 font-mono border ${edited && bulkEdits[key]?.size_label !== undefined ? "border-amber-500 bg-amber-50" : "border-stone-200"} focus:border-sky-700 focus:outline-none px-1`}
                                                                                data-testid={`cell-size-${s.size_id}`}
                                                                            />
                                                                        ) : s.size_label}
                                                                    </td>
                                                                    {/* SKU */}
                                                                    <td className="font-mono text-stone-500">
                                                                        {editable ? (
                                                                            <input
                                                                                value={cellValue(key, "sku", s.sku || "")}
                                                                                onChange={(e) => setCell(key, "sku", e.target.value)}
                                                                                onFocus={(e) => e.target.select()}
                                                                                placeholder="—"
                                                                                className={`w-24 h-7 font-mono border ${edited && bulkEdits[key]?.sku !== undefined ? "border-amber-500 bg-amber-50" : "border-stone-200"} focus:border-sky-700 focus:outline-none px-1`}
                                                                                data-testid={`cell-sku-${s.size_id}`}
                                                                            />
                                                                        ) : (s.sku || "—")}
                                                                    </td>
                                                                    {/* RATE */}
                                                                    <td className="text-right font-mono">
                                                                        {editable ? (
                                                                            <input
                                                                                type="number"
                                                                                inputMode="decimal"
                                                                                step="0.01"
                                                                                min={0}
                                                                                value={cellValue(key, "rate", s.rate)}
                                                                                onChange={(e) => setCell(key, "rate", e.target.value)}
                                                                                onFocus={(e) => e.target.select()}
                                                                                className={`w-20 h-7 text-right font-mono border ${edited && bulkEdits[key]?.rate !== undefined ? "border-amber-500 bg-amber-50" : "border-stone-200"} focus:border-sky-700 focus:outline-none px-1`}
                                                                                data-testid={`cell-rate-${s.size_id}`}
                                                                            />
                                                                        ) : formatINR(s.rate)}
                                                                    </td>
                                                                    {/* OP QTY (derived, read-only) */}
                                                                    <td className="text-right font-mono text-stone-400" title="Derived from past period — change Cl. Qty to adjust">{s.opening_qty}</td>
                                                                    <td className="text-right font-mono text-stone-400">{formatINR(s.opening_value)}</td>
                                                                    {/* IN/OUT (derived) */}
                                                                    <td className="text-right font-mono text-emerald-700" title="Derived from invoices / inwards entries">{s.inwards_qty}</td>
                                                                    <td className="text-right font-mono text-emerald-700">{formatINR(s.inwards_value)}</td>
                                                                    <td className="text-right font-mono text-red-700" title="Derived from invoices / outwards entries">{s.outwards_qty}</td>
                                                                    <td className="text-right font-mono text-red-700">{formatINR(s.outwards_value)}</td>
                                                                    {/* CLOSING QTY */}
                                                                    <td className={`text-right font-mono ${cellQty === 0 ? "text-red-700" : cellQty <= 5 ? "text-amber-700" : "text-stone-700"} font-semibold`}>
                                                                        {editable ? (
                                                                            <input
                                                                                type="number"
                                                                                inputMode="numeric"
                                                                                min={0}
                                                                                value={cellValue(key, "closing_qty", s.closing_qty)}
                                                                                onChange={(e) => setCell(key, "closing_qty", e.target.value)}
                                                                                onFocus={(e) => e.target.select()}
                                                                                className={`w-20 h-9 text-right font-mono font-semibold border-2 ${edited && bulkEdits[key]?.closing_qty !== undefined ? "border-amber-500 bg-amber-50" : "border-stone-200 bg-white"} focus:border-sky-700 focus:outline-none`}
                                                                                data-testid={`bulk-input-${s.size_id}`}
                                                                            />
                                                                        ) : s.closing_qty}
                                                                    </td>
                                                                    {/* CL VALUE — live-recomputed from edited qty × edited rate when in bulkMode */}
                                                                    <td className="text-right font-mono text-sky-700 font-semibold">{formatINR(editable ? liveClosingValue : s.closing_value)}</td>
                                                                    {canManage && (
                                                                        <td className="text-right">
                                                                            <button
                                                                                onClick={() => openEdit(r, s)}
                                                                                className="text-amber-700 hover:text-amber-900 hover:bg-amber-50 border border-amber-300 px-1.5 py-0.5 inline-flex items-center"
                                                                                data-testid={`reg-edit-size-${s.size_id}`}
                                                                                title="Edit variant in full dialog"
                                                                            >
                                                                                <Pencil className="w-3 h-3" />
                                                                            </button>
                                                                        </td>
                                                                    )}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                    {!busy && rows.length > 0 && (
                        <tfoot>
                            <tr className="bg-sky-700 text-white text-sm">
                                <td colSpan={3} className="p-2 font-semibold uppercase tracking-widest text-[11px]">Grand total</td>
                                <td className="p-2 text-right font-mono">{totals.opening_qty}</td>
                                <td className="p-2 text-right font-mono">{formatINR(totals.opening_value)}</td>
                                <td className="p-2 text-right font-mono">{totals.inwards_qty}</td>
                                <td className="p-2 text-right font-mono">{formatINR(totals.inwards_value)}</td>
                                <td className="p-2 text-right font-mono">{totals.outwards_qty}</td>
                                <td className="p-2 text-right font-mono">{formatINR(totals.outwards_value)}</td>
                                <td className="p-2 text-right font-mono font-semibold">{totals.closing_qty}</td>
                                <td className="p-2 text-right font-mono font-semibold">{formatINR(totals.closing_value)}</td>
                                {canManage && <td></td>}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Edit variant + closing stock dialog */}
            <Dialog open={!!editSize} onOpenChange={(o) => !o && setEditSize(null)}>
                <DialogContent className="rounded-none max-w-xl" data-testid="edit-variant-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit variant</DialogTitle>
                        <DialogDescription>
                            {editSize && <>
                                <strong>{editSize.product.product_name}</strong> · {editSize.product.category}
                                <span className="block mt-1 text-xs">Currently: {editSize.size.closing_qty} units @ {formatINR(editSize.size.rate)} = <span className="text-sky-700 font-mono">{formatINR(editSize.size.closing_value)}</span></span>
                            </>}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Size label</Label>
                                <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="rounded-none border-stone-300 mt-1 font-mono" data-testid="edit-variant-label" />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">SKU</Label>
                                <Input value={editSku} onChange={(e) => setEditSku(e.target.value)} placeholder="e.g. ARG-30C-30ML" className="rounded-none border-stone-300 mt-1 font-mono" data-testid="edit-variant-sku" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">HSN code</Label>
                                <Input value={editHsn} onChange={(e) => setEditHsn(e.target.value)} placeholder="e.g. 30049014" className="rounded-none border-stone-300 mt-1 font-mono" data-testid="edit-variant-hsn" />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Rate ₹ (retail)</Label>
                                <Input type="number" min={0} step="0.01" value={editRate} onChange={(e) => setEditRate(e.target.value)} className="rounded-none border-stone-300 mt-1 font-mono" data-testid="edit-variant-rate" />
                            </div>
                        </div>
                        <div className="border-t border-stone-200 pt-3 grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Closing stock</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    value={editQty}
                                    onChange={(e) => setEditQty(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    className="rounded-none border-2 border-amber-400 focus:border-sky-700 mt-1 text-lg font-mono font-semibold h-11 bg-amber-50 focus:bg-white"
                                    data-testid="edit-stock-qty"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-widest text-stone-500">Reason / note (if changed)</Label>
                                <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="e.g. Physical count, GRN #123, breakage" className="rounded-none border-stone-300 mt-1" data-testid="edit-stock-note" />
                            </div>
                        </div>
                        <p className="text-[11px] text-stone-500 border-t border-stone-200 pt-2">
                            Stock-count changes are logged as inwards/outwards (visible in the activity log). Variant changes (label / SKU / HSN / rate) are saved via the product update endpoint and also activity-logged.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditSize(null)} className="rounded-none" data-testid="edit-stock-cancel">Cancel</Button>
                        <Button onClick={saveStock} disabled={saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="edit-stock-save">
                            <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Full product editor */}
            <ProductEditor
                open={!!editProductId}
                onOpenChange={(o) => !o && setEditProductId(null)}
                productId={editProductId}
                onSaved={() => load()}
            />

            {/* Delete product confirm */}
            <AlertDialog open={!!deleteProduct} onOpenChange={(o) => !o && setDeleteProduct(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete '{deleteProduct?.product_name}'?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This removes the product and all its size variants from the catalog. Past invoices, orders and packing slips that referenced it are preserved.
                            <span className="block mt-2 text-amber-700 text-xs">
                                Tip — prefer marking the product as <em>inactive</em> from <strong>/admin/products</strong> for true write-offs.
                            </span>
                            <span className="block mt-2 text-xs">This action is logged for admins.</span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="reg-delete-cancel">Keep</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDeleteProduct} disabled={saving} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="reg-delete-confirm">
                            Delete product
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function lastMonth() {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}
function quarter() {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const start = new Date(now.getFullYear(), q * 3, 1);
    return { from: start.toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) };
}
function fiscalYear() {
    // India FY: 1 April → 31 March
    const now = new Date();
    const year = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    return { from: `${year}-04-01`, to: new Date().toISOString().slice(0, 10) };
}

const Th = ({ children, cls = "", colSpan }) => (
    <th colSpan={colSpan} className={`p-2 text-xs uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>
);
const Num = ({ n, cls = "" }) => <td className={`p-2 text-right font-mono ${cls}`}>{n}</td>;
const Val = ({ v, cls = "" }) => <td className={`p-2 text-right font-mono ${cls}`}>{formatINR(v)}</td>;

const KPI = ({ label, value, sub, cls = "", big = false }) => (
    <div className="bg-white border border-sky-100 p-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className={`font-serif ${big ? "text-3xl" : "text-2xl"} mt-1 ${cls}`}>{value}</div>
        <div className="text-[11px] text-stone-500 mt-1">{sub}</div>
    </div>
);
